import { ReactElement, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultActionMethods, WindowNames } from '@cashier/cashier-model';
import {
  convertToUrl,
  eventBus,
  EventFactory,
  ProviderWindowService,
} from '@cashier/cashier-shared';
import { DefaultLoader } from '@cashier/cashier-ui';

import { useHiddenFormContext } from '../../../contexts';
import { FallbackLink } from '../../fallbackLink';

export interface PaymentProgressProps {
  isWebView: boolean;
  loadingPageUrl: string;
  paymentMethodName: string;
  handleFallbackInitiated: (submitForm: () => void) => void;
  handlePaymentCancelled: () => void;
  applyAction: (
    sendRequest: (
      method: ResultActionMethods,
      url: string,
      data: { [key: string]: string },
    ) => void,
    renderContent: (content: string) => void,
  ) => void;
}

export const PaymentProgress = ({
  applyAction,
  isWebView,
  loadingPageUrl,
  paymentMethodName,
  handleFallbackInitiated,
  handlePaymentCancelled,
}: PaymentProgressProps): ReactElement => {
  const { t } = useTranslation();
  const { submitForm } = useHiddenFormContext();

  const sendRequest = useCallback(
    (
      method: ResultActionMethods,
      url: string,
      data: { [key: string]: string },
    ) => {
      if (ResultActionMethods.GET === method) {
        ProviderWindowService.redirect(convertToUrl(url, data));

        return;
      }

      if (isWebView) {
        eventBus.emit(EventFactory.submitForm(url, method.toString(), data));
      } else {
        ProviderWindowService.initialize(WindowNames.Window, loadingPageUrl);

        submitForm();
      }
    },
    [isWebView, loadingPageUrl, submitForm],
  );
  const renderContent = (output: string) => {
    ProviderWindowService.initialize(WindowNames.Window, 'about:blank');

    ProviderWindowService.writeContent(WindowNames.Window, output);
  };

  useEffect(() => {
    applyAction(sendRequest, renderContent);
  }, [applyAction, sendRequest]);

  return (
    <div className="shown">
      <div className="processing-result">
        <p
          className="processing-result__message"
          data-test="processing-message"
        >
          {t('waitingFor.message', { paymentMethod: paymentMethodName })}
        </p>

        <div className="processing-result__defaultCashierLoader">
          <DefaultLoader />
        </div>

        <FallbackLink
          version="v1"
          handleClick={() => handleFallbackInitiated(submitForm)}
        />

        <button
          className="processing-result__instruction processing-result__instruction__cancel"
          type="button"
          onClick={handlePaymentCancelled}
        >
          {t('cancel.button')}
        </button>
      </div>
    </div>
  );
};
